import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Select, Space } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import {
  countActiveBrowseFilters,
  defaultBrowseFilters,
  normalizePickerDateRange,
  quickRangePresets,
} from '../../utils/oversightFilters';
import { TEAM_ASSISTANT_OVERLAY_Z_INDEX } from '../../utils/teamAssistantZIndex';
import './team-assistant-advanced-filters-modal.css';

const { RangePicker } = DatePicker;

const messageRoleOptions = [
  { value: 'all', label: 'All messages' },
  { value: 'user', label: 'Staff questions' },
  { value: 'assistant', label: 'Assistant replies' },
];

const replyStatusOptions = [
  { value: 'all', label: 'Any status' },
  { value: 'answered', label: 'Answered' },
  { value: 'unanswered', label: 'Unanswered' },
];

const quickDateKeys = ['all', 'today', 'last7', 'last30'];

const TeamAssistantAdvancedFiltersModal = ({
  open,
  onClose,
  filters,
  onApply,
  staff = [],
}) => {
  const [draft, setDraft] = useState(() => defaultBrowseFilters());

  useEffect(() => {
    if (open) {
      const merged = { ...defaultBrowseFilters(), ...filters };
      setDraft({
        ...merged,
        dates: normalizePickerDateRange(merged.dates),
      });
    }
  }, [open, filters]);

  const staffOptions = staff.map((s) => ({
    value: s.user?.id,
    label: s.user?.name || `User ${s.user?.id}`,
  }));

  const activeCount = useMemo(() => countActiveBrowseFilters(draft), [draft]);

  const handleApply = () => {
    onApply({
      ...draft,
      day: null,
      dates: normalizePickerDateRange(draft.dates),
    });
    onClose();
  };

  const handleReset = () => {
    setDraft({
      ...defaultBrowseFilters(),
      dates: null,
    });
  };

  const applyQuickDate = (key) => {
    const preset = quickRangePresets[key];
    setDraft((prev) => ({
      ...prev,
      quickPreset: key,
      dates: preset.range(),
      day: null,
    }));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      centered
      zIndex={TEAM_ASSISTANT_OVERLAY_Z_INDEX}
      destroyOnClose
      className="team-assistant-advanced-filters-modal team-assistant-advanced-filters-modal--large"
      title={
        <div className="team-assistant-advanced-modal-head">
          <FilterOutlined className="team-assistant-advanced-modal-icon-inline" />
          <span className="team-assistant-advanced-modal-title">Advanced filters</span>
          {activeCount > 0 && (
            <span className="team-assistant-advanced-modal-badge">{activeCount} active</span>
          )}
        </div>
      }
      footer={null}
      styles={{
        header: { marginBottom: 0 },
        body: { maxHeight: 'none', overflow: 'visible', padding: 0 },
      }}
    >
      <Form
        layout="vertical"
        size="large"
        className="team-assistant-advanced-form"
        requiredMark={false}
      >
        <div className="team-assistant-advanced-grid">
          <div className="team-assistant-advanced-cell team-assistant-advanced-cell--full">
            <Form.Item label="When" className="team-assistant-advanced-field">
              <div className="team-assistant-advanced-when-row">
                <RangePicker
                  value={normalizePickerDateRange(draft.dates)}
                  onChange={(dates) =>
                    setDraft((prev) => ({
                      ...prev,
                      dates: normalizePickerDateRange(dates),
                      day: null,
                      quickPreset: dates?.[0] && dates?.[1] ? 'custom' : 'all',
                    }))
                  }
                  format="MMM D, YYYY"
                  className="team-assistant-advanced-range"
                  allowEmpty={[true, true]}
                />
                <div className="team-assistant-advanced-quick-dates">
                  {quickDateKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`team-assistant-advanced-quick-chip ${
                        draft.quickPreset === key && !draft.day ? 'is-active' : ''
                      }`}
                      onClick={() => applyQuickDate(key)}
                    >
                      {quickRangePresets[key].label}
                    </button>
                  ))}
                </div>
              </div>
            </Form.Item>
          </div>

          <div className="team-assistant-advanced-cell">
            <Form.Item label="Team member" className="team-assistant-advanced-field">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Everyone"
                value={draft.userId}
                onChange={(userId) => setDraft((prev) => ({ ...prev, userId }))}
                options={staffOptions}
              />
            </Form.Item>
          </div>

          <div className="team-assistant-advanced-cell">
            <Form.Item label="Message type" className="team-assistant-advanced-field">
              <Select
                value={draft.messageRole || 'all'}
                onChange={(messageRole) => setDraft((prev) => ({ ...prev, messageRole }))}
                options={messageRoleOptions}
              />
            </Form.Item>
          </div>

          <div className="team-assistant-advanced-cell">
            <Form.Item label="Search in text" className="team-assistant-advanced-field">
              <Input
                allowClear
                prefix={<SearchOutlined className="team-assistant-advanced-input-icon" />}
                placeholder="Keyword…"
                value={draft.keyword}
                onChange={(e) => setDraft((prev) => ({ ...prev, keyword: e.target.value }))}
              />
            </Form.Item>
          </div>

          <div className="team-assistant-advanced-cell">
            <Form.Item label="Assistant reply" className="team-assistant-advanced-field">
              <Select
                value={draft.replyStatus || 'all'}
                onChange={(replyStatus) => setDraft((prev) => ({ ...prev, replyStatus }))}
                options={replyStatusOptions}
              />
            </Form.Item>
          </div>
        </div>
      </Form>

      <footer className="team-assistant-advanced-modal-footer">
        <Button
          type="link"
          htmlType="button"
          className="team-assistant-advanced-reset"
          onClick={handleReset}
        >
          Reset all
        </Button>
        <Space size={12}>
          <Button size="large" onClick={onClose}>Cancel</Button>
          <Button size="large" type="primary" onClick={handleApply}>
            Apply filters
          </Button>
        </Space>
      </footer>
    </Modal>
  );
};

export default TeamAssistantAdvancedFiltersModal;
